import { useId, useState } from 'react'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { uploadEvidenceItem } from '../../api/evidence'
import { ApiError } from '../../api/problem'
import { EVIDENCE_TYPE_LABEL } from '../../lib/labels'
import type { EvidenceUploadResult } from '../../types/evidence'

export interface EvidenceUploadFormProps {
  caseId: string
  onUploaded: (result: EvidenceUploadResult) => void
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

/**
 * Real multipart upload to `POST /cases/{caseId}/evidence-items/upload` (`EvidenceItemController
 * .upload`, evidence-vault story FR-M14.2) — a genuine byte round-trip through the AV scan +
 * object store, never a mocked/simulated upload. A clean scan lands the item as `draft` (still
 * needs promotion before it can satisfy a requirement); an infected one lands it `quarantined`,
 * shown honestly rather than hidden. Every field this form collects is required server-side
 * (`CreateEvidenceItemRequest`-shaped validation on the upload path too) — the submit button
 * stays disabled until all four plus a file are present, so a 422 round-trip is the exception, not
 * the norm.
 */
export function EvidenceUploadForm({ caseId, onUploaded }: EvidenceUploadFormProps) {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState('')
  const [contemporaneous, setContemporaneous] = useState(true)
  const [region, setRegion] = useState('')
  const [retentionPolicy, setRetentionPolicy] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<EvidenceUploadResult | null>(null)
  const typeListId = useId()

  const canSubmit = !busy && type.trim() !== '' && region.trim() !== '' && retentionPolicy.trim() !== '' && !!file

  function reset() {
    setType('')
    setContemporaneous(true)
    setRegion('')
    setRetentionPolicy('')
    setFile(null)
  }

  async function handleSubmit() {
    if (!canSubmit || !file) return
    setBusy(true)
    setError(null)
    try {
      const result = await uploadEvidenceItem(caseId, { type: type.trim(), contemporaneous, region: region.trim(), retentionPolicy: retentionPolicy.trim() }, file)
      setLastResult(result)
      onUploaded(result)
      reset()
    } catch (err) {
      setError(errorMessage(err, 'Could not upload this evidence file. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button type="button" className="iw-btn iw-btn--primary iw-btn--sm" onClick={() => setOpen(true)}>
        <Icon name="upload" size={12} /> Upload evidence
      </button>
    )
  }

  return (
    <form
      aria-label="Upload evidence"
      onSubmit={(e) => {
        e.preventDefault()
        void handleSubmit()
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--iw-space-3)',
        padding: 'var(--iw-space-4)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius)',
        background: 'var(--iw-neutral-100)',
      }}
    >
      {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {lastResult && !error ? (
        <InlineAlert severity="success">
          Uploaded &ldquo;{lastResult.file.filename}&rdquo; — status {lastResult.item.status}, AV scan{' '}
          {lastResult.file.avScanStatus}.
        </InlineAlert>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
        <div className="iw-field" style={{ flex: '1 1 200px' }}>
          <label className="iw-field-label" htmlFor="evidence-upload-type">
            Type
          </label>
          <input
            id="evidence-upload-type"
            className="iw-input"
            list={typeListId}
            value={type}
            onChange={(e) => setType(e.target.value)}
            placeholder="e.g. timesheet"
            required
          />
          <datalist id={typeListId}>
            {Object.keys(EVIDENCE_TYPE_LABEL).map((key) => (
              <option key={key} value={key}>
                {EVIDENCE_TYPE_LABEL[key]}
              </option>
            ))}
          </datalist>
        </div>

        <div className="iw-field" style={{ flex: '1 1 160px' }}>
          <label className="iw-field-label" htmlFor="evidence-upload-region">
            Region
          </label>
          <input
            id="evidence-upload-region"
            className="iw-input"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="e.g. us-east"
            required
          />
        </div>

        <div className="iw-field" style={{ flex: '1 1 160px' }}>
          <label className="iw-field-label" htmlFor="evidence-upload-retention">
            Retention policy
          </label>
          <input
            id="evidence-upload-retention"
            className="iw-input"
            value={retentionPolicy}
            onChange={(e) => setRetentionPolicy(e.target.value)}
            placeholder="e.g. standard-7yr"
            required
          />
        </div>

        <div className="iw-field" style={{ flex: '1 1 160px', justifyContent: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
            <input
              type="checkbox"
              checked={contemporaneous}
              onChange={(e) => setContemporaneous(e.target.checked)}
            />
            Contemporaneous
          </label>
        </div>
      </div>

      <div className="iw-field">
        <label className="iw-field-label" htmlFor="evidence-upload-file">
          File
        </label>
        <input
          id="evidence-upload-file"
          type="file"
          aria-label="Evidence file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--iw-space-2)' }}>
        <button
          type="button"
          className="iw-btn"
          onClick={() => {
            setOpen(false)
            setError(null)
            reset()
          }}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="iw-btn iw-btn--primary"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  )
}
